import React, {useMemo} from 'react';
import {FormControl, Link} from '@mui/material';
import {gql, useQuery} from '@apollo/client';
import {flatten, groupBy} from 'lodash-es';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  ActionType,
  AutoField,
  SelectAutocompleteField,
  SelectField,
} from '../../../components/AutoForm/AutoFieldNew';
import Loading from '../../../components/Loading/Loading';
import {FEE_FRAGMENT} from '../../../graphql/fragments';
import {InitialFees} from '../../../schema-types/InitialFees';
import {useRecommendContext} from './CreateRecommendHelpers';
import {DAInitialFeeField} from './DAInitialFees';

export const InitialFeeField: React.FC<{
  formikKeyPrefixWithoutFee: string;
  employers?: boolean;
  disabled?: boolean;
}> = ({formikKeyPrefixWithoutFee: formikKeyPrefix, disabled, employers = false}) => {
  const {isSPPDA} = useRecommendContext();
  if (isSPPDA) {
    return (
      <DAInitialFeeField
        formikKeyPrefixWithFee={`${formikKeyPrefix}.${
          employers ? 'legacyInitialEmployerFee' : 'legacyInitialFee'
        }`}
        employers={employers}
        disabled={disabled}
      />
    );
  }

  return (
    <OneFeeInitialFeeField
      formikKeyPrefixWithFee={`${formikKeyPrefix}.${
        employers ? 'initialEmployerFee' : 'initialFee'
      }`}
      employers={employers}
      disabled={disabled}
    />
  );
};

export const OneFeeInitialFeeField: React.FC<{
  formikKeyPrefixWithFee: string;
  employers?: boolean;
  disabled?: boolean;
}> = ({formikKeyPrefixWithFee: formikKeyPrefix, disabled, employers = false}) => {
  const {account} = useAccountContext();
  const {data: initialFeesData} = useQuery<InitialFees>(gql`
    query InitialFees {
      initialFeeComponents {
        ...FeeFragment
      }
    }
    ${FEE_FRAGMENT}
  `);
  const initialFeeOptions = useMemo<SelectAutocompleteField>(() => {
    const groups = groupBy(initialFeesData?.initialFeeComponents, 'calculationBasis');
    groups['Fixed fee'] = (groups['Fixed fee'] || []).sort((a, b) => {
      if (a.name?.startsWith('£') && b.name?.startsWith('£')) {
        if (a.amount && b.amount) {
          return Number(a.amount) > Number(b.amount) ? 1 : -1;
        }
      } else if (a.name?.startsWith('£')) {
        return -1;
      } else if (b.name?.startsWith('£')) {
        return 1;
      }
      if (!a.name) {
        return -1;
      }
      if (!b.name) {
        return 1;
      }
      return 0;
    });
    groups['Percentage fee'] = (groups['Percentage fee'] || []).sort((a, b) => {
      if (!a.name) {
        return -1;
      }
      if (!b.name) {
        return 1;
      }
      return a.name > b.name ? 1 : -1;
    });

    const flattenedGroups = flatten(Object.values(groups)).map((option) => {
      return {
        value: option.id!,
        label: option.name!,
        group: option.calculationBasis!,
      };
    });
    return {
      type: ActionType.selectAutocomplete,
      disabled,
      id: formikKeyPrefix,
      fullWidth: true,
      label: employers ? 'Employer Initial Fee' : 'Initial Fee',
      options: flattenedGroups,
    };
  }, [initialFeesData, disabled, employers]);

  if (!formikKeyPrefix.match(/initialFee|initialEmployerFee/i)) {
    return (
      <div>
        Component does not include `initialFee` or `initialEmployerFee` in
        formikKeyPrefix. formikKeyPrefix is: {formikKeyPrefix}
      </div>
    );
  }
  if (!account) {
    return <Loading entityName="fees" />;
  }

  return (
    <FormControl
      data-test={employers ? 'employers-fee-type' : 'personal-fee-type'}
      fullWidth
      variant="filled"
      size="small"
    >
      <AutoField field={initialFeeOptions} />
      <Link
        href="https://saltus.freshdesk.com/support/solutions/articles/201000067251-which-initial-fees-can-we-charge-"
        target="_blank"
        rel="noreferrer"
        variant="caption"
      >
        Which Initial Fees can we charge?
      </Link>
    </FormControl>
  );
};
